import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { ApiError } from '@/lib/api';
import { CaseTableRow } from '@/pages/bacen/CasesPage';
import { Figure, bacenCasesHref, useCodeLabel, useFormats } from '@/pages/bacen/shared';
import { fetchBacenEntity } from '@/services/bacen';

/**
 * One company, by its full CNPJ: its enforcement history, its registry
 * entry, its ledger rows and the settlement documents whose title names it
 * (a name match, flagged as such, never a join). Persons have no such page.
 */
export function BacenEntityProfilePage() {
  const { cnpj = '' } = useParams();
  const { t } = useTranslation(['bacen', 'common']);
  const { number, currency, percent } = useFormats();
  const label = useCodeLabel();
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['bacen', 'entity', cnpj],
    queryFn: () => fetchBacenEntity(cnpj),
    enabled: cnpj.length > 0,
    // A 404 is an answer (no such CNPJ in the ledger), not a transient failure.
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <div className="p-6">
        <ErrorMessage
          message={notFound ? t('bacen:entity.notFound') : error.message}
          onRetry={notFound ? undefined : () => void refetch()}
        />
      </div>
    );
  }

  const e = data.entity;
  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <p className="text-xs text-muted-foreground">
          <Link to="/bacen/entities" className="text-primary underline-offset-2 hover:underline">
            {t('bacen:entity.back')}
          </Link>
        </p>
        <h1 className="text-lg font-semibold text-foreground">{e.name}</h1>
        <p className="text-sm tabular-nums text-muted-foreground">
          {t('bacen:entity.subtitle', { cnpj: e.cnpj })}
        </p>
      </header>

      <ExplainerCard i18nKey="bacen:entity.explainer" />

      <AsOfStrip asOf={data.as_of} />

      <section className="rounded-lg border bg-card p-4" data-history>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('bacen:entity.history.title')}
        </h2>
        <Answer answer={data.history} scope="record" help={t('bacen:entity.history.help')}>
          {(h) => (
            <div className="space-y-3">
              <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Figure
                  label={t('bacen:entity.history.rows')}
                  value={number.format(h.rows)}
                  href={bacenCasesHref({ entity: e.cnpj })}
                  second={t('bacen:entity.history.processes', {
                    n: number.format(h.processes),
                  })}
                  testId="rows"
                />
                <Figure
                  label={t('bacen:entity.history.ruled')}
                  value={`${number.format(h.ruled)} (${percent.format(h.ruled_share)})`}
                  second={t('bacen:entity.history.walk', {
                    reduced: number.format(h.reduced),
                    maintained: number.format(h.maintained),
                    eliminated: number.format(h.eliminated),
                    typeChanged: number.format(h.type_changed),
                  })}
                />
                <Figure
                  label={t('bacen:entity.history.firstTotal')}
                  value={currency.format(h.first_total)}
                />
                <Figure
                  label={t('bacen:entity.history.finalTotal')}
                  value={currency.format(h.final_total)}
                  second={`${h.first_year ?? '—'}–${h.last_year ?? '—'}`}
                />
              </dl>
              <p className="text-xs text-muted-foreground" data-penalty-codes>
                {Object.entries(h.by_penalty_code)
                  .map(([code, n]) => `${label('penalty', code)} ${number.format(n)}`)
                  .join(' · ')}
              </p>
              <p className="text-xs text-muted-foreground" data-status-families>
                {Object.entries(h.by_status_family)
                  .map(([code, n]) => `${label('statusFamily', code)} ${number.format(n)}`)
                  .join(' · ')}
              </p>
            </div>
          )}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="bacen:entity.guide.history" />
        </div>
      </section>

      <section className="rounded-lg border bg-card p-4" data-registry>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('bacen:entity.registry.title')}
        </h2>
        <Answer answer={data.registry} scope="record" help={t('bacen:entity.registry.help')}>
          {(r) => (
            <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Figure
                label={t('bacen:entity.registry.set')}
                value={
                  r.registry_set
                    ? t(`bacen:codes.registrySet.${r.registry_set}`, {
                        defaultValue: r.registry_set,
                      })
                    : '—'
                }
              />
              <Figure
                label={t('bacen:entity.registry.segment')}
                value={r.registry_segment ?? '—'}
              />
              <Figure label={t('bacen:entity.registry.name')} value={r.registry_name ?? '—'} />
              <Figure label={t('bacen:entity.registry.uf')} value={r.registry_uf ?? '—'} />
            </dl>
          )}
        </Answer>
      </section>

      <section className="rounded-lg border bg-card" data-cases>
        <div className="px-4 py-2 text-xs text-muted-foreground">
          {t('bacen:entity.cases.count', {
            n: number.format(data.cases.length),
          })}
        </div>
        <div className="max-h-[60vh] overflow-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-muted text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">{t('bacen:cases.columns.pas')}</th>
                <th className="px-3 py-2 font-medium">{t('bacen:cases.columns.accused')}</th>
                <th className="px-3 py-2 font-medium">{t('bacen:cases.columns.office')}</th>
                <th className="px-3 py-2 font-medium">{t('bacen:cases.columns.first')}</th>
                <th className="px-3 py-2 font-medium">{t('bacen:cases.columns.board')}</th>
                <th className="px-3 py-2 text-right font-medium">
                  {t('bacen:cases.columns.firstFine')}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {t('bacen:cases.columns.boardFine')}
                </th>
                <th className="px-3 py-2 font-medium">{t('bacen:cases.columns.status')}</th>
              </tr>
            </thead>
            <tbody>
              {data.cases.map((row) => (
                <CaseTableRow key={row.row_index} row={row} currency={currency} />
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-lg border bg-card p-4" data-settlements>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('bacen:entity.settlements.title')}
        </h2>
        {data.settlements.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('bacen:entity.settlements.none')}</p>
        ) : (
          <>
            <p className="mb-2 text-xs text-muted-foreground">
              {t('bacen:entity.settlements.nameMatched')}
            </p>
            <ul className="space-y-1 text-sm">
              {data.settlements.map((s) => (
                <li key={s.document_url} className="flex flex-wrap gap-x-3">
                  <a
                    href={s.document_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary underline-offset-2 hover:underline"
                  >
                    {s.title}
                  </a>
                  <span className="tabular-nums text-muted-foreground">
                    {s.reference_date ?? '—'}
                  </span>
                  {s.is_amendment ? (
                    <span className="text-xs text-muted-foreground">
                      {t('bacen:settlements.amendment')}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          </>
        )}
      </section>
    </div>
  );
}
